import { MouseEventHandler, forwardRef, ForwardedRef } from 'react';
import cn from 'classnames';
import { Presentation } from '@raydiant/api-client-js';
import Spinner from '@raydiant/raydial/components/Spinner';
import AlertIcon from 'raydiant-elements/core/AlertIcon';
import usePresentationFileStatus from '../../../../../hooks/usePresentationFileStatus';
import PresentationIcon from '../../../../../components/PresentationIcon';
import SidebarMenuTooltip from '../../../../../components/Sidebar/SidebarMenuTooltip';
import PlaylistItemDropIndicatorV2 from './PlaylistItemDropIndicatorV2';
import PlaylistItemPresentationActionsV2 from './PlaylistItemPresentationActionsV2';
import RecentlyAddedIndicatorV2 from './RecentlyAddedIndicatorV2';
import Text from '@raydiant/raydial/components/Text';

interface PlaylistItemPresentationV2Props {
  presentation: Presentation;
  indent?: number;
  selected?: boolean;
  disabled?: boolean;
  dropPosition?: 'top' | 'bottom';
  hideActions?: boolean;
  onClick?: MouseEventHandler;
  onEditClick?: MouseEventHandler;
  onTagsClick?: () => void;
  onDuplicateClick?: () => void;
  onSeeInLibraryClick?: () => void;
  seeInLibraryDisabled?: boolean;
  onRemoveClick?: () => void;
  showRulesAndTags?: boolean;
  className?: string;
  isUploading?: boolean;
  isRecentlyAdded?: boolean;
}

const PlaylistItemPresentationV2 = forwardRef<
  HTMLDivElement,
  PlaylistItemPresentationV2Props
>(
  (
    {
      presentation,
      indent = 0,
      selected = false,
      disabled = false,
      dropPosition,
      hideActions = false,
      onClick,
      onEditClick,
      onTagsClick,
      onDuplicateClick,
      onSeeInLibraryClick,
      seeInLibraryDisabled = false,
      onRemoveClick,
      showRulesAndTags,
      className,
      isUploading = false,
      isRecentlyAdded = false,
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const status = usePresentationFileStatus(presentation);
    const showLoading = isUploading || status.isUploading;

    return (
      <div ref={ref} className="relative pb-0.5">
        {dropPosition && (
          <PlaylistItemDropIndicatorV2 position={dropPosition} />
        )}
        {isRecentlyAdded && <RecentlyAddedIndicatorV2 />}
        <div
          className={cn(
            'relative group/item flex flex-row h-12 items-center px-3 shrink-0 rounded-lg border-2 justify-center',
            selected ? 'cursor-pointer !border-blue-600' : 'border-gray-100',
            disabled && 'bg-[#eeeeee] text-[#999999]',
            className,
          )}
          onClick={onClick}
        >
          {status.hasError && (
            <SidebarMenuTooltip
              placement="top"
              title="The upload didn't complete. Please delete this file and upload again."
              arrow
            >
              <span>
                <AlertIcon className="absolute top-[5px] left-[6px] h-[15px] w-[15px] z-[1]" />
              </span>
            </SidebarMenuTooltip>
          )}

          <div className="flex items-center gap-2 overflow-hidden">
            <div className="flex items-center justify-center h-6 w-6 shrink-0 relative rounded-md overflow-hidden [&>div]:rounded-md">
              {showLoading ? (
                <Spinner className="size-4" />
              ) : (
                <PresentationIcon presentation={presentation} />
              )}
            </div>
            <div
              className={cn(
                'flex items-center min-w-0 overflow-hidden',
                disabled && 'text-inherit',
              )}
            >
              <Text
                variant="body"
                className="text-black block truncate"
                truncate
              >
                {presentation.name}
              </Text>
            </div>
          </div>

          <div className="flex-1" />

          {!hideActions && (
            <div className="h-full flex items-center justify-end min-w-[126px]">
              <PlaylistItemPresentationActionsV2
                onEditClick={onEditClick}
                onTagsClick={onTagsClick}
                onDuplicateClick={onDuplicateClick}
                onSeeInLibraryClick={onSeeInLibraryClick}
                seeInLibraryDisabled={seeInLibraryDisabled}
                onRemoveClick={onRemoveClick}
                showRulesAndTags={showRulesAndTags}
              />
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default PlaylistItemPresentationV2;
